import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { Theme, UiTheme } from '../../theme'
import { isKey } from '../../utils/index'

import { Avatar } from '../avatar'

const wrapperStyles = {
  medium: css({
    width: '30px',
    height: '30px',
    position: 'relative',
    zIndex: 200,
  }),
  large: css({
    width: '60px',
    height: '60px',
    position: 'relative',
    zIndex: 200,
  }),
}

const getStyle = (theme: UiTheme, number: number, visibleItems: number) => ({
  display: 'flex',
  position: 'absolute',
  top: 0,
  left: 0,
  willChange: 'transform',
  zIndex: 200,
  flexShrink: 0,
  transform: `translateX(${Math.min(number - 1, visibleItems - 1) * -5}px)`,
  '& > *': {
    flexShrink: 0,
    willChange: 'transform',
    position: 'absolute !important',
    top: 0,
    transition: `transform ${theme.settings.transitions.regularFast}, box-shadow ${theme.settings.transitions.regularFast}`,
    boxShadow: '0 0 0 1px #ffff',
  },
  ...Array.from({ length: visibleItems })
    .map((_, i) =>
      i > 0
        ? {
            [`& > *:nth-child(${i + 1})`]: {
              transform: `translateX(${i * 5}px)`,
            },
          }
        : null
    )
    .reduce((acc, style) => Object.assign(acc, style), {}),
})

interface IAvatarStackProps {
  children: React.ReactNode
  size?: 'medium' | 'large'
  style?: Rule
  visibleItems?: number
}

export class AvatarStack extends React.Component<IAvatarStackProps> {
  static propTypes = {
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOf(['medium', 'large']),
    style: PropTypes.object,
    visibleItems: PropTypes.number,
  }

  static defaultProps = {
    size: 'medium',
    visibleItems: 3,
  }

  render() {
    const visibleItems = this.props.visibleItems
    const children = React.Children.toArray(this.props.children)

    if (!visibleItems) {
      return null
    }

    const visibleChildren =
      children.length > visibleItems
        ? [
            children.slice(0, visibleItems - 1),
            <Avatar initials={`+${children.length - (visibleItems - 1)}`} key="extra" />,
          ]
        : children

    return (
      <Theme>
        {theme => (
          <div
            {...css(isKey(wrapperStyles, this.props.size) && wrapperStyles[this.props.size], this.props.style)}
            role="img"
          >
            <div {...css(getStyle(theme, children.length, visibleItems))}>{visibleChildren}</div>
          </div>
        )}
      </Theme>
    )
  }
}
